import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Table } from "@pentabd/ui";

import useActionCreator from "@redux/hooks/useActionCreator";

import {
  DeleteConfirmationModal,
  ErrorModal,
  SearchBox,
  SelectControllerVertical,
  SuccessModal,
} from "@components";
import { TableColumn } from "./TableColumn";

import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";
import { useUser } from "@hooks/useUser";

import { TablePagination } from "@helpers/tablePagination";
import {
  getConvertedOfficeAddress,
  getConvertedPositionData,
  getConvertedUnitData,
} from "@helpers/baseDataConvert";

import { BASE_DATA } from "@constants/base-data";
import { usePositionsInfo } from "@hooks/HRM/positions/usePositionsInfo";
import { PaginationType } from "@type/HRM/pagination";
import { getAccess } from "@helpers/permissionController";

const { DESIGNATIONS, WINGS, OFFICE } = BASE_DATA;

export const PositionsTable = () => {
  // const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | null>(null);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [itemId, setItemId] = useState<number>();

  const { data: designation } = useBaseData(DESIGNATIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: office } = useBaseData(OFFICE);

  const { language } = useLanguage();

  const { user } = useUser();
  const { realm_roles: roles } = user;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { control, register, setValue } = useForm();

  const { positionsInfo, pagination, handleQuery, deleteSuccess, deleteError } =
    usePositionsInfo();

  const { page } = pagination || ({} as PaginationType);

  const { HRMActionsHandlers } = useActionCreator();
  const { deletePositionsInfo } = HRMActionsHandlers;

  // useEffect(() => {
  //   handleQuery("positionOrder", sortOrder);
  // }, [sortOrder]);

  const successAction = () => {
    setIsSuccessModalOpen(false);
  };
  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const closeConfirmationModel = () => setIsConfirmationModalOpen(false);

  const deleteAction = () => {
    closeConfirmationModel();
    deletePositionsInfo(itemId as number);
  };

  useEffect(() => {
    if (deleteError) {
      setIsErrorModalOpen(true);
    } else if (deleteSuccess === true) {
      setIsSuccessModalOpen(true);
      handleQuery("page", page);
    }
  }, [deleteError, deleteSuccess]);

  useEffect(() => {
    const reset = () => {
      setIsConfirmationModalOpen(false);
      setIsSuccessModalOpen(false);
      setIsErrorModalOpen(false);
    };
    reset();
  }, []);

  return (
    <div>
      <>
        {isSuccessModalOpen && (
          <SuccessModal
            isModalOpen={isSuccessModalOpen}
            action={successAction}
          />
        )}
        {isErrorModalOpen && (
          <ErrorModal
            isModalOpen={isErrorModalOpen}
            closeModal={closeErrorModal}
            subtitle={!!deleteError && deleteError?.response?.data?.message}
          />
        )}
        {isConfirmationModalOpen && (
          <DeleteConfirmationModal
            isModalOpen={isConfirmationModalOpen}
            onClose={closeConfirmationModel}
            action={deleteAction}
          />
        )}
      </>

      <Table
        columns={TableColumn(
          t,
          setIsConfirmationModalOpen,
          setItemId,
          roles,
          getAccess,
          navigate,
        )}
        rows={positionsInfo || []}
        header={{
          badge: {
            label: `${pagination?.total || 0}`,
            size: "sm",
            type: "primary",
          },
          title: t("POSITION_LIST.TABLE.HEADER"),
          buttons: [
            <SearchBox
              key="search"
              control={control}
              placeholder="POSITION_LIST.TABLE.SEARCH"
              handleQuery={handleQuery as any}
            />,
          ],
        }}
        headerExtension={{
          leftComponents: [
            <div className="row">
              <SelectControllerVertical
                control={control}
                label="POSITION_LIST.TABLE.FILTER.ASSIGNED_EMPLOYEE"
                name="isAvailable"
                options={[
                  {
                    label:
                      language === "bn"
                        ? "বরাদ্দকৃত কর্মচারী"
                        : "Assigned Employee",
                    value: "true",
                  },
                  {
                    label:
                      language === "bn"
                        ? "বরাদ্দকৃত কর্মচারী নয়"
                        : "Not Assigned Employee",
                    value: "false",
                  },
                ]}
                register={register}
                setValue={setValue}
                isSearchable
                selectAction={(e) => {
                  handleQuery("isAvailable", e);
                }}
                portal
                cols={6}
                withoutLabel
              />

              <SelectControllerVertical
                control={control}
                label="PROMOTION_CANDIDATES.TABLE.FORM.OFFICE"
                name="addressId"
                options={getConvertedOfficeAddress(office, language)}
                register={register}
                setValue={setValue}
                isSearchable
                selectAction={(e) => {
                  handleQuery("addressId", e);
                }}
                portal
                cols={6}
                withoutLabel
              />
            </div>,
            // <Button
            //   key="order"
            //   fill="outline"
            //   type="light"
            //   size="sm"
            //   onClick={() =>
            //     setSortOrder((prev) => {
            //       let queryValue: "ASC" | "DESC" | null = null;
            //       switch (prev) {
            //         case null:
            //           queryValue = "ASC";
            //           break;
            //         case "ASC":
            //           queryValue = "DESC";
            //           break;
            //         default:
            //           queryValue = null;
            //           break;
            //       }
            //       return queryValue;
            //     })
            //   }
            // >
            //   <Text>{t("POSITION_LIST.TABLE.FILTER")}</Text>
            //   {sortOrder === "ASC" ? (
            //     <IconChevronDown size="16" />
            //   ) : sortOrder === "DESC" ? (
            //     <IconChevronUp size="16" />
            //   ) : (
            //     <></>
            //   )}
            // </Button>,
          ],
          rightComponents: [
            <div className="row promotion-candidate-list">
              <SelectControllerVertical
                control={control}
                label="PROMOTION_CANDIDATES.TABLE.FORM.DESIGNATION"
                name="positionId"
                options={getConvertedPositionData(designation, language)}
                register={register}
                setValue={setValue}
                isSearchable
                selectAction={(e) => {
                  handleQuery("positionId", e);
                }}
                portal
                cols={6}
                withoutLabel
              />

              <SelectControllerVertical
                control={control}
                label="PROMOTION_CANDIDATES.TABLE.FORM.UNIT"
                name="unitId"
                options={getConvertedUnitData(
                  wings.filter((item: any) => item?.name !== "ACC"),
                  language,
                )}
                register={register}
                setValue={setValue}
                isSearchable
                selectAction={(e) => {
                  handleQuery("unitId", e);
                }}
                portal
                cols={6}
                withoutLabel
              />
            </div>,
          ],
        }}
        // edit={{
        //   customIcon: <IconEdit01 size={24} fill="subtitle1" />,
        //   onClick: (id) => navigate(`${id}`),
        // }}
        // remove={
        //   getAccess(roles, "POSITION_MODIFY_PAGE")
        //     ? {
        //         customIcon: <IconTrash01 size={24} fill="danger" />,
        //         onClick: (id) => {
        //           setIsConfirmationModalOpen(true);
        //           setItemId(id as number);
        //         },
        //       }
        //     : { onClick() {} }
        // }
        pagination={TablePagination(pagination, handleQuery)}
      />
    </div>
  );
};
